import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { PrimaryTirePart } from "../../../queries/useSearchTiresByVehicle";
import { useDealerAccountInfo } from "../../../stores/hooks/useDealerAccountInfo";
import { MileageDisplay } from "../../../ui/DistanceDisplay/MileageDisplay";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { Placeholder } from "../../../ui/Placeholder";
import { useTireCategoryOptions } from "../Results/Filters/Category/useTireCategoryOptions";

type ProductInfoProps = {
  className?: string;
  isFront: boolean;
  isRear: boolean;
  part: PrimaryTirePart | null;
  rowClassName?: string;
};

const unsupportedVendors = new Set(["mercedes"]);

export const TirePartDetails: FunctionComponent<ProductInfoProps> = ({
  className,
  isFront,
  isRear,
  part,
  rowClassName,
}) => {
  const { t } = useTranslation();
  const { dealerCountry, dealerDefaultTemplate } = useDealerAccountInfo();
  const mileageWarranty = part?.tireTreadlifeWarranty[dealerCountry];
  const category = useTireCategoryOptions().filter(({ value }) =>
    part?.tireCategoryTags.includes(value)
  );

  return (
    <div className={className}>
      <KeyValue className={rowClassName}>
        <>
          {t(
            isFront
              ? "compare.frontPartNumber"
              : isRear
              ? "compare.rearPartNumber"
              : "compare.partNumber"
          )}
          :
        </>
        <Placeholder className="w-8" loading={!part}>
          <>{part?.tireManufacturerPartNumber}</>
        </Placeholder>
      </KeyValue>
      {part?.tireNationalPartNumber && (
        <KeyValue className={rowClassName}>
          <>{t("compare.natPart")}:</>
          <Placeholder className="w-8" loading={!part}>
            <>{part.tireNationalPartNumber}</>
          </Placeholder>
        </KeyValue>
      )}
      <KeyValue className={rowClassName}>
        <>{t("compare.tireSize")}:</>
        <Placeholder className="w-8" loading={!part}>
          <>
            {[part?.tireVehicleClass, part?.formattedTiresize]
              .filter(Boolean)
              .join("")
              .trim()}
          </>
        </Placeholder>
      </KeyValue>
      <KeyValue className={rowClassName}>
        <>{t("searchResults.loadSpeedPly")}:</>
        <Placeholder className="w-8" loading={!part}>
          <>
            {part?.tireLoadRating}/{part?.tireSpeedIndex}/{part?.tirePlyRating}
          </>
        </Placeholder>
      </KeyValue>
      {Boolean(category.length) && (
        <KeyValue className={rowClassName}>
          <>{t("compare.category")}:</>
          <>{category.map(({ name }) => name).join(", ")}</>
        </KeyValue>
      )}
      {mileageWarranty && !unsupportedVendors.has(dealerDefaultTemplate) && (
        <KeyValue className={rowClassName}>
          <>{t("compare.mileageWarranty")}:</>
          <MileageDisplay>{mileageWarranty}</MileageDisplay>
        </KeyValue>
      )}
    </div>
  );
};
